/**
 * Trailing debounce. `call` schedules `fn` to run `wait` ms after the last call,
 * with the most recent args. `wait <= 0` runs synchronously. `flush` runs a
 * pending call now; `cancel` drops it. Timer-based and framework-agnostic so it
 * is unit-testable with fake timers.
 */
export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  wait: number
): { call: (...args: A) => void; flush: () => void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: A | null = null;

  const clear = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };
  const run = () => {
    if (pending) {
      const args = pending;
      pending = null;
      fn(...args);
    }
  };

  return {
    call(...args: A) {
      pending = args;
      if (wait <= 0) {
        run();
        return;
      }
      clear();
      timer = setTimeout(() => {
        timer = null;
        run();
      }, wait);
    },
    flush() {
      clear();
      run();
    },
    cancel() {
      clear();
      pending = null;
    },
  };
}
